import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  changeAdminRound,
  closeRegistration,
  completeAdminTurnResult,
  createAdminNextTurn,
  getAdminCurrentTurn,
  getAdminGameById,
  getAdminGameHealth,
  getAdminGameHistory,
  getAdminGameReadiness,
  getAdminGameReport,
  getAdminGameState,
  getAdminOperationalWarnings,
  getAdminRoundState,
  openAdminPenalty,
  openRegistration,
  pauseGame,
  resetAdminLiveState,
  skipAdminActiveTurn,
  forceAdminCompleteResult,
  forceAdminResolvePenalty,
  resolveAdminDare,
  startRoundOne,
  updateAdminGame,
} from "../../../features/game/gameApi";
import AdminTurnControlPanel from "../../../components/admin/game/AdminTurnControlPanel";
import AdminRoundControlPanel from "../../../components/admin/game/AdminRoundControlPanel";
import AdminRecoveryPanel from "../../../components/admin/game/AdminRecoveryPanel";
import AdminHistoryPanel from "../../../components/admin/game/AdminHistoryPanel";
import AdminLeaderboardPanel from "../../../components/admin/game/AdminLeaderboardPanel";
import AdminAuditPanel from "../../../components/admin/game/AdminAuditPanel";
import AdminOperationalWarnings from "../../../components/admin/game/AdminOperationalWarnings";
import cls from "./AdminGameSinglePage.module.scss";

const statusOptions = [
  { value: "draft", label: "Draft" },
  { value: "registration_open", label: "Registration Open" },
  { value: "round_live", label: "Round Live" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Completed" },
];

const statusLabelMap = Object.fromEntries(
  statusOptions.map((option) => [option.value, option.label]),
);

const toLocalDateTimeInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  const offset = parsed.getTimezoneOffset() * 60 * 1000;
  return new Date(parsed.getTime() - offset).toISOString().slice(0, 16);
};

const createFormState = (game) => ({
  title: game?.title || "",
  slug: game?.slug || "",
  joinName: game?.joinName || "",
  password: "",
  status: game?.status || "draft",
  currentRound: game?.currentRound ?? 1,
  maxRounds: game?.maxRounds ?? 4,
  allowSelfieUpload: Boolean(game?.allowSelfieUpload),
  avatarSetKey: game?.avatarSetKey || "default",
  startedAt: toLocalDateTimeInput(game?.startedAt),
  endedAt: toLocalDateTimeInput(game?.endedAt),
  settings: {
    coinCostPerLoss: game?.settings?.coinCostPerLoss ?? 1,
    afterRound1CoinsDisabled: game?.settings?.afterRound1CoinsDisabled ?? true,
  },
});

export default function AdminGameSinglePage() {
  const { gameId } = useParams();
  const [game, setGame] = useState(null);
  const [form, setForm] = useState(() => createFormState(null));
  const [adminState, setAdminState] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stateActionLoading, setStateActionLoading] = useState("");
  const [statusMessageDraft, setStatusMessageDraft] = useState("");
  const [message, setMessage] = useState("");
  const [turnView, setTurnView] = useState(null);
  const [turnActionLoading, setTurnActionLoading] = useState(false);
  const [roundState, setRoundState] = useState(null);
  const [health, setHealth] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [adminHistory, setAdminHistory] = useState(null);
  const [adminLeaderboard, setAdminLeaderboard] = useState([]);
  const [operationalWarnings, setOperationalWarnings] = useState(null);
  const [activeTab, setActiveTab] = useState("main");

  const loadGame = useCallback(
    async (showLoader = false) => {
      if (showLoader) setPageLoading(true);

      try {
        const [
          gameData,
          stateData,
          turnData,
          roundData,
          healthData,
          readinessData,
          historyData,
          reportData,
          warningData,
        ] = await Promise.all([
          getAdminGameById(gameId),
          getAdminGameState(gameId),
          getAdminCurrentTurn(gameId),
          getAdminRoundState(gameId),
          getAdminGameHealth(gameId),
          getAdminGameReadiness(gameId),
          getAdminGameHistory(gameId, { page: 1, limit: 20 }),
          getAdminGameReport(gameId),
          getAdminOperationalWarnings(gameId),
        ]);
        const nextGame = gameData.game || null;
        setGame(nextGame);
        setForm(createFormState(nextGame));
        setAdminState(stateData || null);
        setStatusMessageDraft(stateData?.liveState?.statusMessage || "");
        setTurnView(turnData || null);
        setRoundState(roundData || null);
        setHealth(healthData || null);
        setReadiness(readinessData || null);
        setAdminHistory(historyData || null);
        setAdminLeaderboard(reportData?.leaderboard || []);
        setOperationalWarnings(warningData || null);
      } finally {
        if (showLoader) setPageLoading(false);
      }
    },
    [gameId],
  );

  useEffect(() => {
    loadGame(true);
  }, [loadGame]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateSetting = (key, value) => {
    setForm((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        [key]: value,
      },
    }));
  };

  const submitForm = async (nextValues = form) => {
    setSaving(true);
    setMessage("");

    try {
      const payload = {
        title: nextValues.title.trim(),
        slug: nextValues.slug.trim().toLowerCase(),
        joinName: nextValues.joinName.trim(),
        status: nextValues.status,
        currentRound: Number(nextValues.currentRound),
        maxRounds: Number(nextValues.maxRounds),
        allowSelfieUpload: Boolean(nextValues.allowSelfieUpload),
        avatarSetKey: nextValues.avatarSetKey.trim(),
        startedAt: nextValues.startedAt
          ? new Date(nextValues.startedAt).toISOString()
          : null,
        endedAt: nextValues.endedAt
          ? new Date(nextValues.endedAt).toISOString()
          : null,
        settings: {
          coinCostPerLoss: Number(nextValues.settings.coinCostPerLoss),
          afterRound1CoinsDisabled: Boolean(
            nextValues.settings.afterRound1CoinsDisabled,
          ),
        },
      };

      if (nextValues.password.trim()) {
        payload.password = nextValues.password.trim();
      }

      const data = await updateAdminGame(gameId, payload);
      setGame(data.game || null);
      setForm(createFormState(data.game || null));
      setMessage("Game details updated.");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await submitForm();
  };

  const handleStateAction = useCallback(
    async (actionKey) => {
      const actionMap = {
        openRegistration,
        closeRegistration,
        startRoundOne,
        pause: pauseGame,
      };

      const action = actionMap[actionKey];
      if (!action) return;

      setStateActionLoading(actionKey);
      setMessage("");

      try {
        const data = await action(gameId, {
          statusMessage: statusMessageDraft.trim(),
        });
        setAdminState(data || null);
        if (data?.game) {
          setGame(data.game);
          setForm(createFormState(data.game));
        }
        setStatusMessageDraft(data?.liveState?.statusMessage || "");
        setMessage("Game live state updated.");
      } finally {
        setStateActionLoading("");
      }
    },
    [gameId, statusMessageDraft],
  );

  const applyQuickStatus = async (status) => {
    const nextForm = { ...form, status };

    if (status === "registration_open" && !nextForm.startedAt) {
      nextForm.startedAt = toLocalDateTimeInput(new Date().toISOString());
    }

    if (status === "completed" && !nextForm.endedAt) {
      nextForm.endedAt = toLocalDateTimeInput(new Date().toISOString());
    }

    setForm(nextForm);
    await submitForm(nextForm);
  };

  const shiftRound = async (delta) => {
    const maxRounds = Math.max(1, Number(form.maxRounds) || 1);
    const nextRound = Math.min(
      maxRounds,
      Math.max(1, Number(form.currentRound) + delta),
    );
    const nextForm = { ...form, currentRound: nextRound };
    setForm(nextForm);
    await submitForm(nextForm);
  };

  const loadTurnView = useCallback(async () => {
    const data = await getAdminCurrentTurn(gameId);
    setTurnView(data || null);
    return data;
  }, [gameId]);

  const handleCreateNextTurn = useCallback(async () => {
    setTurnActionLoading(true);
    setMessage("");
    try {
      await createAdminNextTurn(gameId, {});
      await loadTurnView();
      await loadGame();
      setMessage("Next turn created.");
    } finally {
      setTurnActionLoading(false);
    }
  }, [gameId, loadGame, loadTurnView]);

  const handleCompleteResult = useCallback(
    async (turnId) => {
      if (!turnId) return;
      setTurnActionLoading(true);
      setMessage("");
      try {
        await completeAdminTurnResult(gameId, turnId, {});
        await loadTurnView();
        await loadGame();
        setMessage("Result moved to penalty stage.");
      } finally {
        setTurnActionLoading(false);
      }
    },
    [gameId, loadGame, loadTurnView],
  );

  const handleOpenPenalty = useCallback(
    async (turnId) => {
      if (!turnId) return;
      setTurnActionLoading(true);
      setMessage("");
      try {
        await openAdminPenalty(gameId, turnId, {});
        await loadTurnView();
        setRoundState(await getAdminRoundState(gameId));
        setMessage("Penalty phase opened.");
      } finally {
        setTurnActionLoading(false);
      }
    },
    [gameId, loadTurnView],
  );

  const handleResolveDareByAdmin = useCallback(
    async (turnId) => {
      if (!turnId) return;
      setTurnActionLoading(true);
      setMessage("");
      try {
        await resolveAdminDare(gameId, turnId, {});
        await loadTurnView();
        setRoundState(await getAdminRoundState(gameId));
        setMessage("Dare resolved by admin.");
      } finally {
        setTurnActionLoading(false);
      }
    },
    [gameId, loadTurnView],
  );

  const handleChangeRound = useCallback(
    async (roundNumber) => {
      if (!Number.isFinite(Number(roundNumber))) return;
      setTurnActionLoading(true);
      setMessage("");
      try {
        await changeAdminRound(gameId, Number(roundNumber), {});
        await loadGame();
        await loadTurnView();
        setRoundState(await getAdminRoundState(gameId));
        setMessage("Round updated.");
      } finally {
        setTurnActionLoading(false);
      }
    },
    [gameId, loadGame, loadTurnView],
  );

  const handleRecoveryAction = useCallback(
    async (actionKey, payload = {}) => {
      const activeTurnId = turnView?.turn?._id || turnView?.turnId;
      if (actionKey === "skip") await skipAdminActiveTurn(gameId, payload);
      if (actionKey === "forceResult" && activeTurnId) {
        await forceAdminCompleteResult(gameId, activeTurnId, payload);
      }
      if (actionKey === "forcePenalty" && activeTurnId) {
        await forceAdminResolvePenalty(gameId, activeTurnId, payload);
      }
      if (actionKey === "reset") await resetAdminLiveState(gameId, payload);

      await loadGame();
      await loadTurnView();
      setMessage("Recovery action applied.");
    },
    [gameId, loadGame, loadTurnView, turnView?.turn?._id, turnView?.turnId],
  );

  const stats = useMemo(() => {
    if (!game) {
      return [
        { label: "Status", value: "-", helper: "Lifecycle state" },
        { label: "Current Round", value: "-", helper: "Live round" },
        { label: "Max Rounds", value: "-", helper: "Configured cap" },
        { label: "Selfie Upload", value: "-", helper: "Player image mode" },
      ];
    }

    return [
      {
        label: "Status",
        value: statusLabelMap[game.status] || game.status,
        helper: "Current lifecycle state",
      },
      {
        label: "Current Round",
        value: String(game.currentRound ?? 1),
        helper: "Round currently selected",
      },
      {
        label: "Max Rounds",
        value: String(game.maxRounds ?? 1),
        helper: "Configured round limit",
      },
      {
        label: "Selfie Upload",
        value: game.allowSelfieUpload ? "Enabled" : "Disabled",
        helper: "Player profile setup mode",
      },
    ];
  }, [game]);

  if (pageLoading) {
    return (
      <div className={cls.page}>
        <div className={cls.orbPink} />
        <div className={cls.orbBlue} />
        <div className={cls.gridGlow} />

        <div className={cls.container}>
          <div className={cls.stateCard}>
            <div className={cls.loader} />
            <h3>Loading game details</h3>
            <p>Please wait while we fetch the selected game session.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className={cls.page}>
        <div className={cls.orbPink} />
        <div className={cls.orbBlue} />
        <div className={cls.gridGlow} />

        <div className={cls.container}>
          <div className={cls.stateCard}>
            <div className={cls.emptyIcon}>?</div>
            <h3>Game not found</h3>
            <p>The requested game session could not be loaded.</p>
            <Link className={cls.secondaryBtn} to="/admin/games">
              Back to Games
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.orbPink} />
      <div className={cls.orbBlue} />
      <div className={cls.gridGlow} />

      <div className={cls.container}>
        <header className={cls.hero}>
          <div className={cls.heroLeft}>
            <span className={cls.kicker}>NOTY ADMIN • GAME CONTROL</span>
            <h1 className={cls.title}>{game.title}</h1>
            <p className={cls.subtitle}>
              Control this game room, update the secure join details, and manage
              the full session flow from one modern dashboard.
            </p>

            <div className={cls.heroMetaRow}>
              <div className={cls.heroMetaCard}>
                <span className={cls.heroMetaLabel}>Game Slug</span>
                <strong>{game.slug || "-"}</strong>
              </div>
              <div className={cls.heroMetaCard}>
                <span className={cls.heroMetaLabel}>Join Name</span>
                <strong>{game.joinName || "-"}</strong>
              </div>
            </div>
          </div>

          <div className={cls.heroRight}>
            <div className={cls.heroStatusRing}>
              <span className={cls.heroStatusValue}>
                {form.currentRound}/{form.maxRounds}
              </span>
              <span className={cls.heroStatusLabel}>Round Progress</span>
            </div>

            <div className={cls.heroActions}>
              <span className={cls.statusBadge}>
                {statusLabelMap[form.status] || form.status}
              </span>

              <Link className={cls.secondaryBtn} to="/admin/games">
                Back to Games
              </Link>
            </div>
          </div>
        </header>

        <section className={cls.statGrid}>
          {stats.map((stat) => (
            <article className={cls.statCard} key={stat.label}>
              <div className={cls.statTopLine} />
              <div className={cls.statLabel}>{stat.label}</div>
              <div className={cls.statValue}>{stat.value}</div>
              <div className={cls.statMeta}>{stat.helper}</div>
            </article>
          ))}
        </section>

        <section className={cls.tabBar}>
          {[
            { key: "main", label: "Main Controller" },
            { key: "rounds", label: "Rounds & Monitor" },
            { key: "ops", label: "Operations" },
            { key: "settings", label: "Game Settings" },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`${cls.tabBtn} ${activeTab === tab.key ? cls.tabBtnActive : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </section>

        {activeTab === "main" ? (
          <>
            <section className={cls.panel}>
              <div className={cls.panelHead}>
                <div>
                  <h2 className={cls.panelTitle}>Live State Controls</h2>
                  <p className={cls.panelSub}>
                    Main controls for registration, game start, pause, and
                    status.
                  </p>
                </div>
                <div className={cls.panelPill}>Main</div>
              </div>

              <div className={cls.liveStateGrid}>
                <article className={cls.liveStateCard}>
                  <span className={cls.liveStateLabel}>Registration</span>
                  <strong className={cls.liveStateValue}>
                    {adminState?.liveState?.registrationStatus || "open"}
                  </strong>
                </article>
                <article className={cls.liveStateCard}>
                  <span className={cls.liveStateLabel}>Phase</span>
                  <strong className={cls.liveStateValue}>
                    {adminState?.liveState?.phase || "lobby"}
                  </strong>
                </article>
                <article className={cls.liveStateCard}>
                  <span className={cls.liveStateLabel}>Players</span>
                  <strong className={cls.liveStateValue}>
                    {adminState?.totals?.totalPlayerCount || 0}
                  </strong>
                </article>
                <article className={cls.liveStateCard}>
                  <span className={cls.liveStateLabel}>Partnered</span>
                  <strong className={cls.liveStateValue}>
                    {adminState?.totals?.partneredCount || 0}
                  </strong>
                </article>
              </div>

              <div className={cls.liveBreakdown}>
                <span>Male: {adminState?.totals?.maleCount || 0}</span>
                <span>Female: {adminState?.totals?.femaleCount || 0}</span>
                <span>Single: {adminState?.totals?.singleCount || 0}</span>
                <span>Pending: {adminState?.totals?.pendingCount || 0}</span>
              </div>

              <label className={cls.field}>
                <span className={cls.label}>Live Status Message</span>
                <input
                  className={cls.input}
                  type="text"
                  maxLength={200}
                  value={statusMessageDraft}
                  onChange={(event) =>
                    setStatusMessageDraft(event.target.value)
                  }
                  placeholder="Optional status note for players"
                />
              </label>

              <div className={cls.liveActionGrid}>
                <button
                  className={cls.secondaryBtn}
                  type="button"
                  disabled={Boolean(stateActionLoading)}
                  onClick={() => handleStateAction("openRegistration")}
                >
                  {stateActionLoading === "openRegistration"
                    ? "Opening..."
                    : "Open Registration"}
                </button>
                <button
                  className={cls.secondaryBtn}
                  type="button"
                  disabled={Boolean(stateActionLoading)}
                  onClick={() => handleStateAction("closeRegistration")}
                >
                  {stateActionLoading === "closeRegistration"
                    ? "Closing..."
                    : "Close Registration"}
                </button>
                <button
                  className={cls.primaryBtn}
                  type="button"
                  disabled={Boolean(stateActionLoading)}
                  onClick={() => handleStateAction("startRoundOne")}
                >
                  {stateActionLoading === "startRoundOne"
                    ? "Starting..."
                    : "Start Game"}
                </button>
                <button
                  className={cls.secondaryBtn}
                  type="button"
                  disabled={Boolean(stateActionLoading)}
                  onClick={() => handleStateAction("pause")}
                >
                  {stateActionLoading === "pause" ? "Pausing..." : "Pause Game"}
                </button>
              </div>
            </section>

            <section className={cls.panel}>
              <div className={cls.panelHead}>
                <div>
                  <h2 className={cls.panelTitle}>Turn Controls</h2>
                  <p className={cls.panelSub}>
                    Monitor active turn and intervene only if required.
                  </p>
                </div>
              </div>
              <AdminTurnControlPanel
                turnView={turnView}
                loading={turnActionLoading}
                onNextTurn={handleCreateNextTurn}
                onCompleteResult={handleCompleteResult}
              />
            </section>
          </>
        ) : null}

        {activeTab === "rounds" ? (
          <>
            <section className={cls.panel}>
              <div className={cls.panelHead}>
                <div>
                  <h2 className={cls.panelTitle}>Round & Penalty Controls</h2>
                  <p className={cls.panelSub}>
                    Monitor rounds, jump rounds, and resolve edge cases.
                  </p>
                </div>
                <div className={cls.panelPill}>Rounds</div>
              </div>
              <AdminRoundControlPanel
                roundState={roundState}
                turnView={turnView}
                loading={turnActionLoading}
                onChangeRound={handleChangeRound}
                onOpenPenalty={handleOpenPenalty}
                onResolveDare={handleResolveDareByAdmin}
              />
            </section>
            <AdminHistoryPanel history={adminHistory} />
            <AdminLeaderboardPanel leaderboard={adminLeaderboard} />
          </>
        ) : null}

        {activeTab === "ops" ? (
          <section className={cls.panel}>
            <div className={cls.panelHead}>
              <div>
                <h2 className={cls.panelTitle}>Operations & Recovery</h2>
                <p className={cls.panelSub}>
                  Monitor operational alerts, run emergency fixes, and review
                  audit events in one control block.
                </p>
              </div>
              <div className={cls.panelPill}>Admin Safety</div>
            </div>

            <div className={cls.opsGrid}>
              <AdminOperationalWarnings warningsPayload={operationalWarnings} />
              <AdminRecoveryPanel
                health={health}
                readiness={readiness}
                onAction={handleRecoveryAction}
              />
            </div>

            <AdminAuditPanel gameId={gameId} />
          </section>
        ) : null}

        {activeTab === "settings" ? (
          <section className={cls.panel}>
            <div className={cls.panelHead}>
              <div>
                <h2 className={cls.panelTitle}>Game Management</h2>
                <p className={cls.panelSub}>
                  Edit the room details, change password access, and tune the
                  session rules.
                </p>
              </div>

              <div className={cls.panelPill}>Live Configuration</div>
            </div>

            {message ? <div className={cls.notice}>{message}</div> : null}

            <form className={cls.formGrid} onSubmit={handleSubmit}>
              <label className={cls.field}>
                <span className={cls.label}>Game Title</span>
                <input
                  className={cls.input}
                  type="text"
                  value={form.title}
                  onChange={(event) => updateField("title", event.target.value)}
                  required
                />
              </label>

              <label className={cls.field}>
                <span className={cls.label}>Slug</span>
                <input
                  className={cls.input}
                  type="text"
                  value={form.slug}
                  onChange={(event) => updateField("slug", event.target.value)}
                  required
                />
              </label>

              <label className={cls.field}>
                <span className={cls.label}>Join Name</span>
                <input
                  className={cls.input}
                  type="text"
                  value={form.joinName}
                  onChange={(event) =>
                    updateField("joinName", event.target.value)
                  }
                  required
                />
              </label>

              <label className={cls.field}>
                <span className={cls.label}>New Password</span>
                <input
                  className={cls.input}
                  type="password"
                  value={form.password}
                  onChange={(event) =>
                    updateField("password", event.target.value)
                  }
                  placeholder="Leave blank to keep current password"
                />
              </label>

              <label className={cls.field}>
                <span className={cls.label}>Status</span>
                <select
                  className={cls.select}
                  value={form.status}
                  onChange={(event) =>
                    updateField("status", event.target.value)
                  }
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className={cls.field}>
                <span className={cls.label}>Avatar Set Key</span>
                <input
                  className={cls.input}
                  type="text"
                  value={form.avatarSetKey}
                  onChange={(event) =>
                    updateField("avatarSetKey", event.target.value)
                  }
                />
              </label>

              <label className={cls.field}>
                <span className={cls.label}>Current Round</span>
                <input
                  className={cls.input}
                  type="number"
                  min="1"
                  max={Math.max(1, Number(form.maxRounds) || 1)}
                  value={form.currentRound}
                  onChange={(event) =>
                    updateField("currentRound", event.target.value)
                  }
                />
              </label>

              <label className={cls.field}>
                <span className={cls.label}>Max Rounds</span>
                <input
                  className={cls.input}
                  type="number"
                  min="1"
                  max="50"
                  value={form.maxRounds}
                  onChange={(event) =>
                    updateField("maxRounds", event.target.value)
                  }
                />
              </label>

              <label className={cls.field}>
                <span className={cls.label}>Started At</span>
                <input
                  className={cls.input}
                  type="datetime-local"
                  value={form.startedAt}
                  onChange={(event) =>
                    updateField("startedAt", event.target.value)
                  }
                />
              </label>

              <label className={cls.field}>
                <span className={cls.label}>Ended At</span>
                <input
                  className={cls.input}
                  type="datetime-local"
                  value={form.endedAt}
                  onChange={(event) =>
                    updateField("endedAt", event.target.value)
                  }
                />
              </label>

              <label className={cls.field}>
                <span className={cls.label}>Coin Cost Per Loss</span>
                <input
                  className={cls.input}
                  type="number"
                  min="0"
                  max="999"
                  value={form.settings.coinCostPerLoss}
                  onChange={(event) =>
                    updateSetting("coinCostPerLoss", event.target.value)
                  }
                />
              </label>

              <div className={cls.toggleColumn}>
                <label className={cls.toggleCard}>
                  <div className={cls.toggleInfo}>
                    <div className={cls.toggleTitle}>Allow Selfie Upload</div>
                    <div className={cls.toggleSub}>
                      Players can upload their own selfie during setup.
                    </div>
                  </div>

                  <input
                    type="checkbox"
                    checked={form.allowSelfieUpload}
                    onChange={(event) =>
                      updateField("allowSelfieUpload", event.target.checked)
                    }
                  />
                </label>

                <label className={cls.toggleCard}>
                  <div className={cls.toggleInfo}>
                    <div className={cls.toggleTitle}>
                      Disable Coins After Round 1
                    </div>
                    <div className={cls.toggleSub}>
                      Prevent further deductions after the first round.
                    </div>
                  </div>

                  <input
                    type="checkbox"
                    checked={form.settings.afterRound1CoinsDisabled}
                    onChange={(event) =>
                      updateSetting(
                        "afterRound1CoinsDisabled",
                        event.target.checked,
                      )
                    }
                  />
                </label>
              </div>

              <div className={cls.formFooter}>
                <button
                  className={cls.primaryBtn}
                  type="submit"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Game Changes"}
                </button>
              </div>
            </form>
          </section>
        ) : null}

        <section className={cls.panel}>
          <div className={cls.panelHead}>
            <div>
              <h2 className={cls.panelTitle}>Game Intelligence</h2>
              <p className={cls.panelSub}>
                Keep an eye on the latest turn logs and current leaderboard to
                make fast moderator decisions.
              </p>
            </div>
            <div className={cls.panelPill}>Live Insights</div>
          </div>

          <div className={cls.insightsGrid}>
            <AdminHistoryPanel history={adminHistory} />
            <AdminLeaderboardPanel leaderboard={adminLeaderboard} />
          </div>
        </section>

        <section className={cls.bottomGrid}>
          <article className={cls.panel}>
            <div className={cls.panelHead}>
              <div>
                <h2 className={cls.panelTitle}>Lifecycle Controls</h2>
                <p className={cls.panelSub}>
                  Change the session status instantly with quick neon actions.
                </p>
              </div>
            </div>

            <div className={cls.chipGrid}>
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  className={cls.actionChip}
                  type="button"
                  disabled={saving || option.value === form.status}
                  onClick={() => applyQuickStatus(option.value)}
                >
                  <span className={cls.actionChipDot} />
                  {option.label}
                </button>
              ))}
            </div>
          </article>

          <article className={cls.panel}>
            <div className={cls.panelHead}>
              <div>
                <h2 className={cls.panelTitle}>Round Controls</h2>
                <p className={cls.panelSub}>
                  Move backward or forward and keep the session under control.
                </p>
              </div>
            </div>

            <div className={cls.roundControlCard}>
              <button
                className={cls.secondaryBtn}
                type="button"
                disabled={saving || Number(form.currentRound) <= 1}
                onClick={() => shiftRound(-1)}
              >
                Previous Round
              </button>

              <div className={cls.roundCenter}>
                <span className={cls.roundOverline}>CURRENT POSITION</span>
                <div className={cls.roundValue}>
                  {form.currentRound} / {form.maxRounds}
                </div>
              </div>

              <button
                className={cls.secondaryBtn}
                type="button"
                disabled={
                  saving ||
                  Number(form.currentRound) >=
                    Math.max(1, Number(form.maxRounds))
                }
                onClick={() => shiftRound(1)}
              >
                Next Round
              </button>
            </div>
          </article>
        </section>
      </div>
    </div>
  );
}
